import React, { memo, useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import ContentCard from '../Cards/ContentCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

//design --
const SliderContainer = styled.div`
  margin-bottom: 40px;
  padding: 0 50px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 0 10px; /* Reduced padding on mobile to make content broader */
    margin-bottom: 30px;
  }
`;

const SliderTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const SliderContentWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const SliderContent = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 10px 0;
  scroll-behavior: smooth;
  gap: 15px;
  scrollbar-width: none; /* Firefox */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  /* Important: This makes all cards take up the same space */
  & > * {
    flex: 0 0 180px;
    
    @media (max-width: 768px) {
      flex: 0 0 140px;
    }
  }
`;

const EmptyMessage = styled.p`
  color: var(--text-dim);
  padding: 20px 0;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  border-radius: var(--border-radius);
  padding: 20px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 20; /* Higher z-index to appear above cards */
  pointer-events: auto; /* Ensure buttons always capture clicks */
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.3);
  
  /* Improve clickable area */
  &::before {
    content: '';
    position: absolute;
    top: -15px;
    bottom: -15px;
    left: -15px;
    right: -15px;
    z-index: -1;
  }
  
  ${SliderContainer}:hover & {
    opacity: 1;
  }
  
  &.prev {
    left: 15px;
  }
  
  &.next {
    right: 15px;
  }
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.8);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    background-color: rgba(0, 0, 0, 0.4);
  }
  
  /* Makes sure the arrow icon is always visible */
  svg {
    font-size: 18px;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.8));
  }
  
  @media (max-width: 768px) {
    display: none; /* Hide navigation buttons on mobile */
  }
`;

const ContentSlider = ({ title, items = [], watchHistory = [] }) => {
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  
  const checkScrollButtons = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10); // 10px buffer
    }
  };
  
  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', checkScrollButtons);
      // Initial check
      checkScrollButtons();
      
      // Check again after images might have loaded
      setTimeout(checkScrollButtons, 1000);
    }
    
    return () => {
      if (slider) {
        slider.removeEventListener('scroll', checkScrollButtons);
      }
    };
  }, [items]);
  
  const scrollLeft = (e) => {
    // Prevent event propagation to avoid triggering card hover
    e.stopPropagation();
    
    if (sliderRef.current) {
      const cardWidth = 195; // 180px card + 15px gap
      sliderRef.current.scrollLeft -= cardWidth * 3; // Scroll 3 cards left
    }
  };
  
  const scrollRight = (e) => {
    // Prevent event propagation to avoid triggering card hover
    e.stopPropagation();
    
    if (sliderRef.current) {
      const cardWidth = 195; // 180px card +15px gap
      sliderRef.current.scrollLeft += cardWidth * 3; // Scroll 3 cards right
    }
  };
  
  if (!items || items.length === 0) {
    return (
      <SliderContainer>
        <SliderTitle>{title}</SliderTitle>
        <EmptyMessage>No content available.</EmptyMessage>
      </SliderContainer>
    );
  }
  
  return (
    <SliderContainer>
      <SliderTitle>{title}</SliderTitle>
      <SliderContentWrapper>
        <NavigationButton 
          className="prev" 
          onClick={scrollLeft}
          disabled={!canScrollLeft}
          aria-label="Previous content"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </NavigationButton>
        
        <SliderContent ref={sliderRef}>
          {items.map(item => {
            if (!item) return null;
            
            const historyItem = watchHistory.find(h => h.id === item._id || h.id === item.id);
            const progress = historyItem ? historyItem.progress : (item.progress || 0);
            const type = item.type || (item.seasons ? 'show' : 'movie');
            
            // Create a new object with all properties from the original item
            const cardItem = {
              ...item,
              _id: item._id || item.id,
              title: item.title || item.showTitle || '',
              image: item.image || item.showImage || '',
              releaseYear: item.releaseYear || item.year || '',
              type: type,
              // Pass view count data
              views: item.views || item.view_count || item.viewCount || 0
            };
            
            return (
              <ContentCard 
                key={cardItem._id} 
                item={cardItem} 
                type={type} 
                progress={progress} 
              />
            );
          })}
        </SliderContent>
        
        <NavigationButton 
          className="next" 
          onClick={scrollRight}
          disabled={!canScrollRight}
          aria-label="Next content"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </NavigationButton>
      </SliderContentWrapper>
    </SliderContainer>
  );
};

//React.memo
export default memo(ContentSlider);